// src/components/CatylexSearch/CatylexProcessingSection.tsx

import * as React from "react";
import { useState, useEffect } from "react";
import { CatylexToolInput } from "../../interfaces/catylexSearchTypes";
import { DownloadIcon, SearchIcon } from "./CatylexIcons";
import { catylexColors } from "./catylexSearchStyles";

interface CatylexProcessingSectionProps {
  isProcessingTool: boolean;
  startTime?: number;
  toolInput?: CatylexToolInput;
}

/**
 * Rung thresholds in seconds, per branch.
 *
 * Read used to be a multi-minute binary download, which is why its rungs were
 * 15/30. It is a bounded JSON API call now — seconds — so it reaches its last
 * rung soonest and stops implying a long wait. Search and the pre-action window
 * are ordinary API calls and match SharePoint's 5/15.
 */
const READ_RUNGS = { second: 4, last: 10 };
const DEFAULT_RUNGS = { second: 5, last: 15 };

/**
 * The glyph box, sized and tinted exactly like the result section's header icon
 * so this row turns into that header rather than being swapped for something
 * unrelated. Both glyphs render at 14px inside it, so the row doesn't shift when
 * the action resolves and the icon changes.
 *
 * The Catylex wordmark used to sit here instead — a ~4:1 image given `height:
 * 22px; width: auto` inside a 22px square, so it spilled out of its box and
 * across the status text. The brand stays where it has room: the copy below
 * ("Connecting to Catylex…", "Loading contract from Catylex…") and the panel
 * that follows.
 */
const iconBoxStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
  borderRadius: "6px",
  backgroundColor: catylexColors.primaryBg,
  flexShrink: 0,
};

/**
 * Catylex-branded processing indicator shown while the AI
 * is executing a catylex_search tool call.
 * Action-aware: searching the repository and loading a contract in full are
 * different waits, so each gets its own icon and progressive messages.
 */
const CatylexProcessingSection: React.FC<CatylexProcessingSectionProps> = ({
  isProcessingTool,
  startTime,
  toolInput,
}) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isProcessingTool || !startTime) {
      setElapsed(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [isProcessingTool, startTime]);

  if (!isProcessingTool) return null;

  const isReadAction = toolInput?.action === "read";

  // The action arrives in input_json_delta chunks, which land *after*
  // isProcessingTool is set on onToolUseStart — and on the Bedrock provider they
  // may never land at all. Without a branch of its own, that window falls through
  // to the search ladder and tells the user a contract load is "searching
  // contracts", which is wrong rather than merely vague.
  const rungs = isReadAction ? READ_RUNGS : DEFAULT_RUNGS;

  const getStatusText = (): string => {
    if (!toolInput) {
      if (elapsed >= rungs.last) return "Taking longer than usual...";
      if (elapsed >= rungs.second) return "Still connecting...";
      return "Connecting to Catylex...";
    }

    if (isReadAction) {
      if (elapsed >= rungs.last) return "Still loading the contract...";
      if (elapsed >= rungs.second) return "Extracting document text...";
      return "Loading contract from Catylex...";
    }

    if (elapsed >= rungs.last) return "Still searching...";
    if (elapsed >= rungs.second) return "Searching contracts... (this may take a moment)";
    return "Searching contracts...";
  };

  return (
    <div style={{ marginBottom: "8px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "8px 12px",
          backgroundColor: catylexColors.mutedBg,
          borderRadius: "8px",
          border: `1px solid ${catylexColors.border}`,
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
        <div style={iconBoxStyle}>
          {isReadAction ? (
            <DownloadIcon size={14} stroke={catylexColors.primary} />
          ) : (
            <SearchIcon stroke={catylexColors.primary} />
          )}
        </div>

        {/* The last rung's copy — "Still searching…", "Still loading the
            contract…" — is the whole long-wait signal now. A live m:ss counter
            used to sit beside it, and a stopwatch ticking next to a stalled
            sentence reads as a hang being timed rather than work in progress.
            The dots carry liveness; the ladder carries duration. */}
        <span
          role="status"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: "13px",
            color: catylexColors.headerText,
            fontWeight: 500,
          }}
        >
          {getStatusText()}
        </span>

        {/* Animated dots */}
        <svg
          width="20"
          height="6"
          viewBox="0 0 20 6"
          aria-hidden="true"
          focusable="false"
          style={{ flexShrink: 0 }}
        >
          <style>
            {`
              @keyframes catylex-dot {
                0%, 80%, 100% { opacity: 0.2; }
                40% { opacity: 1; }
              }
              .catylex-dot-1 { animation: catylex-dot 1.4s ease-in-out infinite; }
              .catylex-dot-2 { animation: catylex-dot 1.4s ease-in-out infinite 0.2s; }
              .catylex-dot-3 { animation: catylex-dot 1.4s ease-in-out infinite 0.4s; }
            `}
          </style>
          <circle className="catylex-dot-1" cx="3" cy="3" r="2.5" fill={catylexColors.primary} />
          <circle className="catylex-dot-2" cx="10" cy="3" r="2.5" fill={catylexColors.primary} />
          <circle className="catylex-dot-3" cx="17" cy="3" r="2.5" fill={catylexColors.primary} />
        </svg>
      </div>
    </div>
  );
};

export default CatylexProcessingSection;
